import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NGXLogger } from 'ngx-logger';
import {
  BookingService,
  ExamEventService,
  NotificationService,
  UserService,
} from 'src/app/services';
import { Supervision } from 'src/app/types';
import { Step3TestTimeFormComponent as DefaultStep3TestTimeFormComponent } from '../../default';
import { Step4aConsentProctoringModalComponent } from '../step4a-consent-proctoring-modal';

@Component({
  selector: 'app-step4-test-time-form-sdv',
  templateUrl: 'step4-test-time-form-sdv.component.html',
  styleUrls: [
    '../../default/step3-test-time-form/step3-test-time-form.component.scss',
  ],
})
export class Step4TestTimeFormSdvComponent extends DefaultStep3TestTimeFormComponent {
  constructor(
    private router: Router,
    protected activatedRoute: ActivatedRoute,
    protected bookingService: BookingService,
    protected examEventService: ExamEventService,
    protected formBuilder: UntypedFormBuilder,
    protected logger: NGXLogger,
    protected modalService: BsModalService,
    protected notificationService: NotificationService,
    protected userService: UserService
  ) {
    super(
      activatedRoute,
      bookingService,
      examEventService,
      formBuilder,
      logger,
      notificationService,
      userService
    );
  }

  async submit(): Promise<boolean> {
    this.form.markAllAsTouched();

    const eventId = this.examEventChosen.value;
    const isProctoringEvent =
      this.examEventService.listState.response.items.find(
        (event) => event.id === eventId
      )?.supervision === Supervision.Proctoring;

    // Ungültiges Formular?
    if (!this.form.valid) {
      return false;
    }

    // Kein neuer Booking-Request nötig
    if (
      this.bookingService.formState.item.testEventId ===
        eventId &&
      this.isBookingCreated
    ) {
      // This is the case where we:
      // - select an event
      // - navigate forward
      // - navigate back to this step
      // - do NOT select a different event
      // - proceed from this step once again
      // In this case, we don't have to notify the backend to create a new booking.

      // Nur ggf. Proctoring-Consent anzeigen
      if (isProctoringEvent) {
        const result =
          await this.showProctoringConsentModal();
        return result;
      }

      return true;
    }

    // Booking vorbereiten
    const current = {
      ...this.bookingService.formState.item,
    };
    current.userId = this.userService.formState.item.id;
    current.testEventId = eventId;
    current.prerequisiteQuestionId =
      this.booking.prerequisiteQuestionId;
    current.priceCalculationData =
      current.priceCalculationData ?? undefined;
    this.bookingService.formState.item = current;

    // Proctoring-Consent Modal?
    if (isProctoringEvent) {
      const consentGiven =
        await this.showProctoringConsentModal();
      if (!consentGiven) return false;
    }

    try {
      await this.bookingService.createBooking();
      this.isBookingCreated = true;
      return true;
    } catch (error) {
      this.isBookingCreated = false;

      if ((error as HttpErrorResponse).status === 409) {
        await this.notificationService.error(
          `booking.${this.tenant}.error-message.event-overbooked`
        );
        await this.loadEvents(this.testId);
      } else {
        await this.fail(error as HttpErrorResponse);
      }

      return false;
    }
  }

  private showProctoringConsentModal(): Promise<boolean> {
    return new Promise((resolve) => {
      const modalRef = this.modalService.show(
        Step4aConsentProctoringModalComponent,
        {
          class: 'modal-lg',
          initialState: { data: false },
        }
      );

      modalRef.onHidden?.subscribe(() => {
        const result = modalRef.content?.result;
        resolve(!!result?.data);
      });
    });
  }

  async cancelBooking(): Promise<void> {
    if (this.bookingId > 0) {
      await this.bookingService
        .deleteBooking(this.bookingId)
        .finally(() => {
          this.bookingService.formState = {
            loading: false,
            item: BookingService.factoryItem(),
          };
        });
    }
    this.router.navigate(['/home']);
  }

  private get bookingId(): number {
    return this.bookingService.formState.item.bookingId;
  }
}
